import { FormattedMessage, useIntl } from 'react-intl';
import { LineItem } from 'shared/types/cart';
import { Button } from 'components/ui/atoms/button';
import { Icon } from 'components/ui/atoms/icons';
import { OpenInNew } from 'components/ui/atoms/icons/OpenInNew';
import { Hyperlink } from 'components/ui/atoms/link';
import { getEnv } from 'env/env';
import { getPriceDetailsFromCart } from 'utils/price';
import {
  AboutCourse,
  AboutProgram,
  OrderAbout,
  OrderSummary,
  StartButton,
} from './components';
import { messages } from './messages';
import { ThankYouProps } from './types';
import {
  getCoursesDetails,
  getLocalCurrencyCharge,
  getPaymentMethod,
} from './utils';

export const ThankYou = ({ order, program }: ThankYouProps) => {
  const { formatMessage } = useIntl();

  const orderNumber = order.orderNumber!;
  const paymentMethod = getPaymentMethod(order.payments);
  const priceDetails = getPriceDetailsFromCart(order);
  const localCurrencyCharge = getLocalCurrencyCharge(order);
  const coursesDetails = getCoursesDetails(
    order.lineItems as Required<LineItem>[],
  );

  const isFreeOrder =
    priceDetails.cartTotal === 0 && order?.payments.length === 0;

  const purchaseText = formatMessage(
    messages[
      isFreeOrder
        ? 'payment.thankyou.purchase.free'
        : 'payment.thankyou.purchase.paid'
    ],
  );

  const purchaseType = program
    ? 'program'
    : order.lineItems[0]?.variant?.attributes?.mode;

  const bodyMessageID =
    purchaseType === 'program'
      ? 'payment.thankyou.main.body.program'
      : purchaseType === 'credit'
        ? 'payment.thankyou.main.body.credit'
        : 'payment.thankyou.main.body.course';

  return (
    <>
      <div className="container-fluid py-5 bg-light-200 px-md-5">
        <div className="row justify-content-center">
          <section className="col-12 col-md-8 p-0">
            <div className="d-flex align-items-center">
              <Icon
                type="check-outline"
                svgProps={{
                  'aria-label': 'Check mark',
                  className: 'mr-2 text-success-400',
                }}
              />
              <div className="h-5 text-success-700 mb-0">
                <FormattedMessage
                  id="payment.thankyou.main.subheading"
                  defaultMessage="Your {purchase} is successful."
                  description="Purchase success message"
                  values={{ purchase: purchaseText }}
                />
              </div>
            </div>
            <h1 className="mt-4 tw-text-3.5xl">
              <FormattedMessage
                id="payment.thankyou.main.heading"
                defaultMessage="Congrats! You're ready to begin learning!"
                description="Encouraging message for learners after successful purchase"
              />
            </h1>
            <p className="tw-mb-4">
              {formatMessage(messages[bodyMessageID], {
                purchase: purchaseText,
              })}{' '}
              {formatMessage(messages['payment.thankyou.main.body.summary'])}
            </p>
            <div className="tw-flex tw-gap-2">
              <StartButton
                program={program}
                courseURL={
                  coursesDetails.length === 1
                    ? coursesDetails[0].url
                    : getEnv(
                        'NEXT_PUBLIC_EDX_LEARNER_DASHBOARD_URL',
                        'https://home.edx.org',
                      )
                }
              />
              {!isFreeOrder && (
                <Button variant="brandOutline" radius="none" asChild>
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href={`${
                      getEnv('NEXT_PUBLIC_EDX_COMMERCE_COORDINATOR_BASE_URL') +
                      getEnv('NEXT_PUBLIC_EDX_CC_ORDER_RECEIPT_PATH')
                    }?order_number=${orderNumber}`}
                  >
                    {formatMessage(messages['payment.thankyou.view.receipt'])}
                    <OpenInNew svgProps={{ className: 'tw-ml-2' }} />
                  </a>
                </Button>
              )}
            </div>
          </section>
        </div>
      </div>
      <div className="container-fluid px-md-5 py-4">
        <div className="row justify-content-center">
          <OrderSummary
            orderNumber={orderNumber}
            priceDetails={priceDetails}
            coursesDetails={coursesDetails}
            paymentMethod={paymentMethod}
            isFreeOrder={isFreeOrder}
            localCurrencyCharge={localCurrencyCharge}
          />
        </div>
        <div className="row justify-content-center py-4">
          <section className="col-12 col-md-8 bg-light-500 rounded py-3">
            {program ? (
              <AboutProgram program={program} />
            ) : (
              <AboutCourse
                {...coursesDetails[0]}
                url={
                  coursesDetails.length === 1
                    ? coursesDetails[0].url
                    : getEnv(
                        'NEXT_PUBLIC_EDX_LEARNER_DASHBOARD_URL',
                        'https://home.edx.org',
                      )
                }
              />
            )}
          </section>
        </div>
        <div className="row justify-content-center py-4">
          <section className="col-12 col-md-8 rounded border py-3">
            <OrderAbout purchaseType={purchaseType} />
          </section>
        </div>
        <div className="row justify-content-center py-4">
          <section className="col-12 col-md-8">
            <Hyperlink
              destination={getEnv('NEXT_PUBLIC_EDX_LEARNER_DASHBOARD_URL')}
            >
              <FormattedMessage
                id="payment.checkout.order.about"
                defaultMessage="Go to dashboard"
                description="CTA to go to the dashboard after successful purchase"
              />
            </Hyperlink>
          </section>
        </div>
      </div>
    </>
  );
};
